import type { SkillDetail } from "@/lib/types";

// The Scan → Security pipeline surfaced as a vertical timeline (mirrors the suggestion pipeline).
// Shared by the upload page (live, while a new skill is reviewed) and the skill detail page (which
// shows this instead of the skill body until the skill goes `active`).
export type PState = "done" | "active" | "failed" | "pending" | "unreached";
export type PhaseCard = { key: string; label: string; state: PState; note: string };

// -- Map a polled skill onto the four visible pipeline phases -----------------
export function phasesFor(s: SkillDetail | null): PhaseCard[] {
  const rej = s?.rejection?.stage; // "scan" | "security" | "error"
  const active = s?.status === "active";
  const rejected = s?.status === "rejected";
  const stage = s?.stage; // "scanning" | "security" | "manual_review" | "done"
  const manualReview = stage === "manual_review";
  const score = s?.security?.score;

  let scan: PState = rej === "scan"
    ? "failed"
    : active || manualReview || stage === "security" || stage === "done"
      ? "done"
      : "active";
  let security: PState = rej === "security"
    ? "failed"
    : active
      ? "done"
      : rejected
        ? "unreached"  // rejected before this phase ran — it was never reached
        : stage === "security"
          ? "active"
          : "pending";
  // A generic "error" stage attaches to whichever phase was in flight.
  if (rej === "error") {
    if (stage === "security") security = "failed";
    else scan = "failed";
  }
  const added: PState = active ? "done" : rejected ? "failed" : "pending";
  // Which stage caused the rejection — surfaced in the "Not added" description.
  const failedStage = scan === "failed" ? "scan" : security === "failed" ? "security" : "";

  return [
    { key: "upload", label: "Uploaded", state: "done", note: "Archive received and unpacked" },
    {
      key: "scan",
      label: "Scanning",
      state: scan,
      note:
        scan === "failed" ? "Failed the structure scan"
        : scan === "done" ? "Structure & SKILL.md look good"
        : "Validating structure & SKILL.md…",
    },
    {
      key: "security",
      label: "Security check",
      state: security,
      note:
        security === "failed" ? "Flagged unsafe content"
        : security === "done" ? `Passed${score != null ? ` — score ${score}` : ""}`
        : security === "active" ? "Scanning for unsafe content…"
        : manualReview ? "Automated review paused — manual review required"
        : security === "unreached" ? "Not reached"
        : "Waiting for the security review",
    },
    {
      key: "added",
      label: added === "failed" ? "Rejected" : "Added",
      state: added,
      note:
        added === "done" ? "Live — your skill is ready"
        : added === "failed"
          ? failedStage ? `Failed in the ${failedStage} stage` : "Rejected during review"
        : manualReview ? "Not live — waiting for manual security review"
        : "Goes live once the checks pass",
    },
  ];
}

// -- Presentation (matches SuggestionProgress's vertical timeline) ------------

const ic = "h-4 w-4";
function CheckIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>); }
function XIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>); }
function DashIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 12h14" /></svg>); }

function Icon({ state }: { state: PState }) {
  const base = "flex h-8 w-8 shrink-0 items-center justify-center rounded-full";
  if (state === "done")
    return <span className={`${base} bg-emerald-500/15 text-emerald-600 dark:text-emerald-400`}><CheckIcon /></span>;
  if (state === "failed")
    return <span className={`${base} bg-red-500/15 text-red-600 dark:text-red-400`}><XIcon /></span>;
  if (state === "active")
    return (
      <span className={`${base} bg-primary/15`}>
        <span className="h-4 w-4 rounded-full border-2 border-accent/30 border-t-accent motion-safe:animate-spin" />
      </span>
    );
  if (state === "unreached")
    return <span className={`${base} border border-dashed border-black/30 bg-black/[0.03] text-text/55 dark:border-white/30 dark:bg-white/[0.04]`}><DashIcon /></span>;
  return (
    <span className={`${base} border border-black/12 bg-black/[0.02] dark:border-white/15 dark:bg-white/[0.03]`}>
      <span className="h-1.5 w-1.5 rounded-full bg-text/25" />
    </span>
  );
}

function noteClass(state: PState): string {
  return state === "failed" ? "text-red-600 dark:text-red-300/90"
    : state === "done" ? "text-emerald-700 dark:text-emerald-300/80"
    : state === "active" ? "text-text/75"
    : state === "unreached" ? "text-text/55"
    : "text-text/45";
}

// One card per phase — the card turns green when that phase completes, red when it fails, primary
// while it's running, and stays neutral (or dashed, if a rejection meant it was never reached)
// otherwise. Tints are semi-transparent so they read in both light and dark mode.
function PhaseCardView({ card }: { card: PhaseCard }) {
  const SKIN: Record<PState, string> = {
    done: "border-emerald-500/45 bg-emerald-500/[0.07]",
    active: "border-primary/50 bg-primary/[0.08]",
    failed: "border-red-500/45 bg-red-500/[0.07]",
    pending: "border-black/10 bg-black/[0.02] opacity-90 dark:border-white/10 dark:bg-white/[0.02]",
    unreached: "border-dashed border-black/25 bg-black/[0.02] dark:border-white/25 dark:bg-white/[0.03]",
  };
  return (
    <div className={`flex items-center gap-3.5 rounded-xl border p-4 transition-colors duration-500 ${SKIN[card.state]}`}>
      <Icon state={card.state} />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${card.state === "pending" ? "text-text/60" : "text-text"}`}>{card.label}</p>
        <p className={`mt-0.5 text-xs leading-snug ${noteClass(card.state)}`}>{card.note}</p>
      </div>
    </div>
  );
}

export default function SkillPipelineProgress({ skill }: { skill: SkillDetail | null }) {
  return (
    <div className="space-y-3">
      {phasesFor(skill).map((p) => (
        <PhaseCardView key={p.key} card={p} />
      ))}
    </div>
  );
}
